import { toast } from "react-toastify";
import { CURRENCY_LOCALE } from "../config/misc";
import { createTheme } from "@mui/material";
import { getDisputes } from "../service/contractActions";
import { DISPUTE_FEE } from "../constants/constants";
import React from "react";
import ToastifyIconComponent from "../components/Common/ToastifyIconComponent";
import { updateChatReadStatus } from "../api/api";
import { readJson, safeJsonParse, writeJson } from "./safeJson";
import { parseUser } from "./storedUser";

// Generic persisted-JSON helpers. Both delegate to safeJson so that NO key can
// be poisoned by a `JSON.stringify(undefined)` write and no read can throw —
// see utils/safeJson.js for why that mattered.
export const getFromLocalStorage = (key) => readJson(key, null);

export const setToLocalStorage = (key, value) => {
  writeJson(key, value);
};

// Function to remove a value from local storage
export const removeFromLocalStorage = (key) => {
  localStorage.removeItem(key);
};

export const deleteAllTempState = () => {
  // Get all keys in local storage
  const keys = Object.keys(localStorage);

  // Filter keys that start with "temp_"
  const tempKeys = keys.filter((key) => key.startsWith("temp_"));

  // Delete each "temp_" item from local storage
  tempKeys.forEach((key) => {
    localStorage.removeItem(key);
  });
}

export const feeAmount = (type) => {
  if (type === "withdraw") return 7;
  if (type === "deposit") return 7;
};

export const toolTipConfig = {
  titles: {
    fee: "$7.00 equivalent fee for bringing money on chain",
    amountReceive: "Your wallet will receive deposit",
    sendDeposit: "Account used to send the fund",
    businessLimit: "Add title",
    collateral: "Add title",
    tiedLimit: "Add title",
    freeLimit: "Add title",
    bankVault: "Add title",
    batchAmount: "Add title",
  },
  props: {
    tooltip: {
      sx: {
        fontSize: '1.3rem',
        backgroundColor: '#3a3a3a',
        color: '#ffffff',
        fontWeight: 500,
      },
    }
  }
};

export const calculateFeesCustodian = (
  amount,
  amountInUsd,
  spotPrice,
  approve
) => {
  const type = "deposit" //it should be in props if fee amount differs for each type
  if (amount === 0) return "0.00";
  else {
    return `${(4.90 * (amount / amountInUsd)).truncateIt()}`;
  }

};

export const calculateFees = (
  amount,
  amountInUsd,
  spotPrice,
  approve
) => {
  const type = "deposit" //it should be in props if fee amount differs for each type
  if (amount === 0) return "0.00";
  else {
    return `${(feeAmount(type) * (amount / amountInUsd)).truncateIt()}`;
  }

};

export const toolTipTheme = createTheme({
  components: {
    MuiTooltip: {
      defaultProps: {
        // open: true, // use this to keep tooltip open to tweak design.
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: "white",
          filter: "drop-shadow(1px 1px 6px rgba(0,0,0,0.2))",
          borderRadius: "4px",
          color: "#090909",
          fontSize: "16px",
          fontWeight: "400",
          position: "relative",
        },
        arrow: {
          color: "white",
          filter: "drop-shadow(1px 1px 6px rgba(0,0,0,0.2))",
        }
      },
    }
  }
  // Documentation for customization https://mui.com/material-ui/api/tooltip/#css
});

export const getRequestId = (transactionData) => {
  return transactionData?.requestId ?? '--';
}

export const getTxHash = (transactionData) => {
  const txHash = transactionData?.transactionHash || transactionData?.txId || transactionData?.txHash;
  if (txHash === undefined) return {};  // Return an empty function instead of false
  const alacrityUrl = `${process.env.REACT_APP_BLOCK_EXPLORER_URL}/tx/${txHash}`;
  return window.open(alacrityUrl);
}

export const preciseSum = (a, b) => {
  if (!!a || !!b) {
    const val1 = a ?? 0.0;
    const val2 = b ?? 0.0;
    // Truncate (never round) the 2dp sum. The old `Math.round(... * 100) / 100`
    // rounded the sum UP at the half-cent before truncating — a round-off. The
    // fixed `truncateIt` is float-safe on its own, so no pre-round snap is needed.
    return (Number(val1) + Number(val2)).truncateIt(2);
  } else return 0.0;
};

export const calculateCustodianEarning = (amount, amountInUsd, type, spotPrice, approve) => {
  //INFO: not using type here but might need it later
  // Custodian gets $4.95 out of $7.00 transaction fee (70% of fee)
  const custodianFeeUsd = 4.90; // Fixed $4.90 per transaction
  if (amount === 0) return "0.00";
  //for only new tx
  else if (!approve && spotPrice) {
    return spotPrice ? (custodianFeeUsd * spotPrice)?.truncateIt().toLocaleString(CURRENCY_LOCALE.format, CURRENCY_LOCALE.fixedPointFormat) : 0;
  } else {
    return `${(custodianFeeUsd * (amount / amountInUsd)).truncateIt().toLocaleString(CURRENCY_LOCALE.format, CURRENCY_LOCALE.fixedPointFormat)}`;

  }
};

export const filterOptions = [
  { value: "1", label: "1 day" },
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days" },
  { value: "All", label: "All" },
];

export const containsOnlySpaces = (str) => str.trim().length === 0;

export const calculateRecieveAmount = (amount, amountInUsd, type, spotPrice, approve) => {
  if (amount === 0) return "0.00";
  else {
    return parseFloat((amount - calculateFees(amount, amountInUsd, spotPrice, approve)).toFixed(2));
  }
};

// eslint-disable-next-line no-extend-native
Number.prototype.truncateIt = function (decimalPlaces = 2) {
  // Fintech: TRUNCATE (floor toward zero), never round. Returns a Number so all
  // existing call-sites (`.toLocaleString(...)`, arithmetic, `|| 0.0`) keep working.
  //
  // We deliberately AVOID `Math.trunc(this * 10^dp) / 10^dp`: that float multiply
  // hits IEEE-754 error and corrupts the last digit in BOTH directions
  // (e.g. 8.165 -> 8.1649 under-state, or one ULP too high -> a rounded-up value).
  // Instead render with guard digits via toFixed (fixed-point, snaps away ~1e-14
  // representation noise without touching real data), then cut the string at `dp`.
  const v = this.valueOf();
  if (!Number.isFinite(v)) return v; // preserve NaN / ±Infinity as before
  const neg = v < 0 ? "-" : "";
  const [int, frac = ""] = Math.abs(v).toFixed(decimalPlaces + 6).split(".");
  const cut = frac.slice(0, decimalPlaces);
  return Number(cut ? `${neg}${int}.${cut}` : `${neg}${int}`);
};
export const tribunalFees = (amount, amountInUsd) => {
  if (amount === 0) return "0.00";
  else return `${(10 * (amount / amountInUsd)).truncateIt().toLocaleString(CURRENCY_LOCALE.format, CURRENCY_LOCALE.fixedPointFormat)}`;
};

export const isObjectEmpty = (objectName) => {
  return Object.keys(objectName).length === 0;
};

export function copyToClipboard(text) {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  document.body.appendChild(textArea);

  textArea.select();
  document.execCommand("copy");

  document.body.removeChild(textArea);
}

export const handleTxCallError = (err) => {
  //Code  when tx took too long/deadline exceeded occurs =>  3080006
  // Chain errors are frequently non-JSON strings ("execution reverted: ...").
  // Parsing them raw threw inside the error handler itself.
  const errorObject = typeof err === "string" ? safeJsonParse(err, {}) : err;
  //Error code 3080006 is deadline exception and throw tx took too long
  if (errorObject?.error?.code === 3080006) {
    toast.error(<div className="content">Transaction took too long,try again</div>, {
      icon: () => (<ToastifyIconComponent />),
      className: "midl-toast",
    });
    return;
  } else {
    toast.error(<div className="content">Please ensure you have minimum required funds for this action. If this error persists please contact us.</div>, {
      icon: () => (<ToastifyIconComponent />),
      className: "midl-toast",
    });
    return;
  }
};

export const getFileExtension = (fileName) => {
  return "." + fileName.split(".").pop().toLowerCase();
};

export const allowedImageExtensions = [".jpg", ".jpeg", ".png", ".avif"];

export const allowedVideoExtensions = [".mp4", ".mkv", ".mov"];

export const allowedFileExtensions = [
  ...allowedImageExtensions,
  ...allowedVideoExtensions,
];

export const checkDisputeBalance = async (requestId, alacrityName, rpc, getUserBalanceFn) => {
  //TODO: For Better Approach return the isDispute variable from getDisputes
  const isDispute = await getDisputes(requestId, rpc);
  if (!isDispute) {
    const userBalance = await getUserBalanceFn(alacrityName, rpc);
    if (!userBalance) {
      return false;
    } else if (userBalance < DISPUTE_FEE) {
      toast.error(<div className="content">Insufficient balance: {userBalance} OPN</div>, {
        icon: () => (<ToastifyIconComponent />),
        className: "midl-toast",
      });
      return false;
    }
  }
  return true;
};

//Util to mark messages notifications as read
export const markMessagesAsRead = async (chatMessages, chatResponse, user, setChatMessages, config) => {
  if (chatMessages.messages.length < 1) return
  else
    console.log(chatMessages, 'chat messages in utils');
  try {
    const lastMessage = chatMessages?.messages[chatMessages.messages.length - 1] || {};
    const loggedInUserName = parseUser(user)?.alacrityName;
    const isUnread = !lastMessage?.readBy.includes(loggedInUserName);

    if (isUnread) {
      const chatId = chatResponse?.data?.data?._id || {};
      const lastMessageId = lastMessage?._id || [];

      // Call the API to update the read status
      await updateChatReadStatus({ chatId, messageId: lastMessageId }, config);

      // Update the last message's readBy array
      const updatedLastMessage = {
        ...lastMessage,
        readBy: [...lastMessage.readBy, loggedInUserName],
      };

      // Update the state with the modified last message
      setChatMessages(prevState => {
        const updatedMessages = [...prevState.messages];
        updatedMessages[updatedMessages.length - 1] = updatedLastMessage;
        return { ...prevState, messages: updatedMessages };
      });
    }
  } catch (error) {
    console.error("Error updating last message read status:", error);
  }
};

export const shortAddress = (addr) => {
  if (!addr) return "-";
  if (!addr.startsWith("0x")) return addr;

  return `${addr.slice(0, 5)}...${addr.slice(-3)}`;
};

/**
 * Format a batch-ledger journey endpoint (the `from`/`to` of a Batch Earning
 * row) for display. v4 pool holders are stored as `v4_pool:<poolId>` (the
 * poolId-keyed ledger) — render them as the pool's friendly pair symbol
 * (e.g. "PKR/GBP Pool") via the `poolSymbolById` map (poolId → symbol, built
 * from the GET /v4-pools registry). Falls back to a shortened poolId when the
 * pool isn't in the registry, and recognizes the legacy `v4_pool:w<ccy>_dai`
 * holders from historical rows. Wallet addresses and pool/role constants
 * (JURY_POOL, CUSTODIAN_POOL, …) keep their existing shortened/lowercased form.
 */
export const formatJourneyParty = (value, poolSymbolById = {}) => {
  if (!value) return "-";
  if (value.startsWith("v4_pool:")) {
    const id = value.slice("v4_pool:".length).trim();
    const key = id.toLowerCase();
    if (poolSymbolById[key]) return `${poolSymbolById[key]} Pool`;
    if (key.startsWith("0x")) return `Pool ${key.slice(0, 6)}…${key.slice(-4)}`;
    // Legacy currency-keyed holder, e.g. "wgbp_dai" / "wgbp_dai 1.00%".
    const legacy = key.match(/^w([a-z]{2,5})_dai/);
    if (legacy) return `${legacy[1].toUpperCase()}/DAI Pool`;
    return value;
  }
  return shortAddress(value).toLowerCase();
};